/**
 * Elegir en qué entidad se quieren ver los exámenes.
 *
 * POR QUE EXISTE
 * ==============
 * Una misma persona puede atenderse en varias IPS, y es lo normal: se hace la
 * ecografía donde se la autorizaron y la tomografía donde había cita. El
 * portal abría la ficha de actividad más reciente y el resto de su historia
 * le quedaba invisible, sin que nada se lo dijera.
 *
 * Un examen que existe y no aparece es peor que un portal vacío: el paciente
 * concluye que se perdió, y llama a la clínica equivocada.
 *
 * CRITERIO DE LA PANTALLA
 * =======================
 * Quien entra aquí puede tener poca práctica navegando. Por eso:
 *
 * - Si solo tiene una entidad, esto no se muestra. No se le explica un
 *   problema que no tiene.
 * - Se dice en palabras de todos los días, no "instituciones" ni "tenant".
 * - Cada opción trae cuántos exámenes hay y de cuándo es el último, que es lo
 *   que le permite reconocer dónde está lo que busca.
 */

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Building2, Check, Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  cambiarDeEntidad,
  entidadesDelPaciente,
  getStoredPatientInfo,
  type EntidadDelPaciente,
} from '@/application/auth/auth.service'
import { toast } from '@/lib/toast'
import { aFechaSegura } from '@/lib/utils'

const cuantosExamenes = (n: number): string =>
  n === 0 ? 'sin exámenes todavía' : n === 1 ? '1 examen' : `${n} exámenes`

const ultimoExamen = (fecha: string | null): string => {
  if (!fecha) return ''
  const d = aFechaSegura(fecha)
  return d ? ` · último el ${d.toLocaleDateString('es-CO')}` : ''
}

export function SelectorDeEntidad() {
  const queryClient = useQueryClient()
  const entidades = entidadesDelPaciente()
  const actual = getStoredPatientInfo()?.id
  const [cambiando, setCambiando] = useState<string | null>(null)

  // Con una sola entidad no hay nada que elegir, y explicarlo sobraría.
  if (entidades.length < 2) return null

  const cambiar = async (entidad: EntidadDelPaciente) => {
    if (entidad.patientId === actual) return
    setCambiando(entidad.patientId)
    try {
      await cambiarDeEntidad(entidad.patientId)
      // Todo lo que hay en pantalla pertenece a la entidad anterior.
      await queryClient.invalidateQueries()
      toast.success(`Ahora está viendo sus exámenes de ${entidad.institutionName}`)
    } catch {
      toast.error('No pudimos cambiar de entidad. Inténtelo de nuevo.')
    } finally {
      setCambiando(null)
    }
  }

  return (
    <Card>
      <CardContent className="py-5">
        <div className="flex items-start gap-3">
          <Building2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-teal-600 dark:text-teal-400" />
          <div className="min-w-0 flex-1">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Tiene exámenes en {entidades.length} entidades
            </p>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Elija dónde quiere verlos. Puede cambiar cuando quiera.
            </p>

            {/* Con muchas entidades la lista no cabe en pantalla y empuja los
                exámenes fuera de la vista, que es justo lo que el paciente
                vino a ver. Se desplaza dentro de su propia caja. Lo normal
                son dos o tres, pero el caso de muchas existe. */}
            <div className="mt-4 max-h-80 space-y-2 overflow-y-auto pr-1">
              {entidades.map((entidad) => {
                const esLaActual = entidad.patientId === actual
                return (
                  <button
                    key={entidad.patientId}
                    type="button"
                    onClick={() => void cambiar(entidad)}
                    disabled={cambiando !== null || esLaActual}
                    aria-current={esLaActual ? 'true' : undefined}
                    className={`flex w-full items-center justify-between gap-3 rounded-lg border p-4 text-left transition-colors ${
                      esLaActual
                        ? 'border-teal-400 bg-teal-50 dark:border-teal-600 dark:bg-teal-950/40'
                        : 'border-gray-200 hover:border-teal-400 hover:bg-teal-50 dark:border-gray-800 dark:hover:bg-teal-950/30'
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-gray-900 dark:text-gray-100">
                        {entidad.institutionName}
                      </span>
                      <span className="block text-sm text-gray-600 dark:text-gray-400">
                        {cuantosExamenes(entidad.estudios)}
                        {ultimoExamen(entidad.ultimoEstudio)}
                      </span>
                    </span>

                    {cambiando === entidad.patientId ? (
                      <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-teal-600" />
                    ) : esLaActual ? (
                      <span className="flex flex-shrink-0 items-center gap-1 text-sm font-medium text-teal-700 dark:text-teal-400">
                        <Check className="h-4 w-4" />
                        Viendo esta
                      </span>
                    ) : (
                      <span className="flex-shrink-0 text-sm font-medium text-teal-700 dark:text-teal-400">
                        Ver estos
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
